import React, { useContext, useMemo } from 'react'
import { EmptyArray } from 'lib/array/arrayUtils'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import DateFloatingPanel from 'components/SearchV2/Components/Mobile/Panels/DateFloatingPanel'
import useDateSearchProps from 'components/SearchV2/Hooks/useDateSearchProps'
import useStepsController from 'components/SearchV2/Hooks/useStepsController'
import LocationFloatingPanel from 'components/SearchV2/Components/Mobile/Panels/LocationFloatingPanel'
import {
  CAR_HIRE_SEARCH_TYPES,
} from 'constants/carHire'
import { searchSteps } from './CarHireMobileSearchControls'
import { useIntl } from 'react-intl'
import useCarHireLabels from 'hooks/CarHire/useCarHireLabels'

interface Props {
  activeStep: searchSteps
  setActiveStep: (step: searchSteps) => void
  onSubmit: () => void
  differentLocations: boolean
}

function CarHireSearchDrawer(props: Props) {
  const { activeStep, setActiveStep, onSubmit, differentLocations } = props
  const {
    searchItem,
    secondarySearchItem,
    checkinDate: pickUpDate,
    checkoutDate: dropOffDate,
  } = useContext(GlobalSearchStateContext)

  const intl = useIntl()
  const {
    pickupDropoffLocationSearchTitleLabel,
    pickupLocationSearchTitleLabel,
    dropoffLocationSearchTitleLabel,
    pickupLocationSearchPlaceholderLabel,
  } = useCarHireLabels()

  const locationTitle = useMemo(() => {
    if (!differentLocations) {
      return pickupDropoffLocationSearchTitleLabel
    }
    if (activeStep === searchSteps.LOCATION) {
      return pickupLocationSearchTitleLabel
    }
    return dropoffLocationSearchTitleLabel
  }, [differentLocations, activeStep, pickupDropoffLocationSearchTitleLabel, pickupLocationSearchTitleLabel, dropoffLocationSearchTitleLabel])

  const specificDatesLabel = intl.formatMessage({
    id: 'vpETUhrqmItAEtzKZCXe',
    defaultMessage: 'Specific dates',
    description: 'Label for the specific dates toggle button in car hire date picker',
  })

  const applyLabel = intl.formatMessage({
    id: 'z-LhielZlz2VTaw5sB8Y',
    defaultMessage: 'Apply',
    description: 'Confirm button text for car hire date selection in the car hire section',
  })

  const whenToHireTitle = intl.formatMessage({
    id: 'zhXZM5tBRcYpC_qsiZ0j',
    defaultMessage: 'When do you want to hire?',
    description: 'Title for the car hire date selection panel in car hire section',
  })

  const activeDateToggleButton = {
    id: DATE_SEARCH_OPTION_IDS.SPECIFIC,
    label: specificDatesLabel,
  }

  const dateSearchProps = useDateSearchProps({ onAnytimeDatesSelected: () => { } })

  const stepsValidation = useMemo(() => ({
    [searchSteps.LOCATION]: searchItem,
    ...differentLocations && { [searchSteps.SECONDARY_LOCATION]: differentLocations && secondarySearchItem },
    [searchSteps.DATE]: pickUpDate && dropOffDate,
  }),
  [searchItem, differentLocations, secondarySearchItem, pickUpDate, dropOffDate])

  const {
    goToNextStep,
    goToPreviousStep,
    closeActiveStep,
  } = useStepsController({ activeStep, setActiveStep, stepsValidation, onSearch: onSubmit })

  return (
    <>
      <LocationFloatingPanel
        isOpen={activeStep === searchSteps.LOCATION || activeStep === searchSteps.SECONDARY_LOCATION}
        onClose={closeActiveStep}
        onGoBack={goToPreviousStep}
        onConfirm={goToNextStep}
        isSecondaryLocation={activeStep === searchSteps.SECONDARY_LOCATION}
        searchTypes={CAR_HIRE_SEARCH_TYPES}
        typeaheadVertical="car_hire"
        placeholder={pickupLocationSearchPlaceholderLabel}
        title={locationTitle}
        confirmButtonDisabled={false}
      />
      <DateFloatingPanel
        {...dateSearchProps}
        isOpen={activeStep === searchSteps.DATE}
        onClose={closeActiveStep}
        onConfirm={closeActiveStep}
        onGoBack={goToPreviousStep}
        confirmText={applyLabel}
        dateToggleButtons={EmptyArray}
        activeDateToggleButton={activeDateToggleButton}
        title={whenToHireTitle}
        label="day"
        includeLastDayOnCount
        allowSameDayRange
      />
    </>
  )
}

export default CarHireSearchDrawer
